import { useEffect, useState } from 'react';

/**
 * "Print handout" - VP8.5, then his ask the same evening: the full print ran 43 pages, so "the person can pick
 * what they want to print, like the sections ... or make it like a simplified version so it's just less pages".
 *
 * The chooser lists exactly the sections the page shows - the SAME ordered list the jump menu is built from, so
 * a section cannot appear in one and not the other. What is unticked carries `data-print-omit` and the print
 * stylesheet drops it, so Ctrl+P honours the same choice. The first time it is the SHORT handout (the overview,
 * the 1031 clock, the open items and the deal terms - every deal still on paper, one line each); after that the
 * reader's own pick is remembered on their device.
 */

export interface PrintSection {
  key: string;
  label: string;
}

/** The short handout: whichever of these a portal has. The terms table keeps every deal on paper, one line each. */
export const SHORT_KEYS = ['overview', 'exchange', 'openItems', 'terms', 'valuation', 'netProceeds'];

/**
 * `keys` is the short list for the page's KIND (TP3): a tenant's short handout leads with his requirement and the
 * best matches, which a buyer's page does not have. Absent, it is the buyer's list - what this always was.
 */
export const shortChoice = (sections: PrintSection[], keys: readonly string[] = SHORT_KEYS): string[] => {
  const short = sections.filter((s) => keys.includes(s.key)).map((s) => s.key);
  return short.length ? short : sections.map((s) => s.key);
};

export const TERMS_HINT = 'The deal terms table keeps every deal on paper, one line each.';

const storageKey = (slug?: string) => `portal-print-sections:${slug ?? 'portal'}`;

/* localStorage is a convenience, never a dependency: private-mode Safari THROWS on read and write, and the
   handout must still print. */
const readChoice = (slug?: string): string[] | null => {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(storageKey(slug)) ?? 'null');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null;
  } catch {
    return null;
  }
};

const saveChoice = (slug: string | undefined, keys: string[]): void => {
  try {
    localStorage.setItem(storageKey(slug), JSON.stringify(keys));
  } catch {
    /* private mode: the pick is simply not remembered next time. */
  }
};

export default function PrintChooser({
  sections,
  slug,
  onPrint = () => window.print(),
  shortKeys = SHORT_KEYS,
  termsHint = TERMS_HINT,
}: {
  sections: PrintSection[];
  slug?: string;
  onPrint?: () => void;
  /** The short handout for this page's kind, and what the hint under the list says about its one-line table. */
  shortKeys?: readonly string[];
  termsHint?: string;
}) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<string[]>(() => {
    // A remembered key the page no longer shows is dropped; a pick with nothing left falls back to the short one.
    const known = (readChoice(slug) ?? []).filter((k) => sections.some((s) => s.key === k));
    return known.length ? known : shortChoice(sections, shortKeys);
  });

  useEffect(() => {
    for (const { key } of sections) {
      const el = document.getElementById(`section-${key}`);
      if (!el) continue;
      if (chosen.includes(key)) el.removeAttribute('data-print-omit');
      else el.setAttribute('data-print-omit', 'true');
    }
  }, [chosen, sections]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const all = sections.map((s) => s.key);
  const short = shortChoice(sections, shortKeys);
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((k) => b.includes(k));
  const toggle = (key: string) =>
    setChosen((c) => (c.includes(key) ? c.filter((k) => k !== key) : all.filter((k) => k === key || c.includes(k))));
  const print = () => {
    saveChoice(slug, chosen);
    setOpen(false);
    onPrint();
  };

  return (
    <div className="portal-print">
      <button type="button" className="portal-print-btn" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        Print handout
      </button>
      {open && (
        <div className="portal-print-panel" role="dialog" aria-label="Choose what to print">
          <p className="portal-print-title">Choose what to print</p>
          <div className="portal-print-presets">
            <button type="button" aria-pressed={same(chosen, short)} onClick={() => setChosen(short)}>
              Short handout
            </button>
            <button type="button" aria-pressed={same(chosen, all)} onClick={() => setChosen(all)}>
              Everything
            </button>
          </div>
          <ul className="portal-print-list">
            {sections.map((s) => (
              <li key={s.key}>
                <label>
                  <input type="checkbox" checked={chosen.includes(s.key)} onChange={() => toggle(s.key)} />
                  {s.label}
                </label>
              </li>
            ))}
          </ul>
          {/* An empty hint is no line at all (a buy search's words carry one: its short handout has no one-line table). */}
          {all.includes('terms') && termsHint.trim() !== '' && (
            <p className="portal-print-hint">{termsHint}</p>
          )}
          <div className="portal-print-actions">
            <button type="button" className="portal-print-cancel" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="button" className="portal-print-go" disabled={chosen.length === 0} onClick={print}>
              Print
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
